import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/api/client';
import { useApi } from '@/hooks/useApi';
import type {
  BulkCheckResponse,
  BulkItem,
  BulkLogItem,
  BulkLogResponse,
  BulkMessagedResponse,
} from '@/api/types';

/** The code every /portal/bulk/* endpoint answers with when the caller isn't allowed. */
export const BULK_NOT_ALLOWED = 'BULK_IMPORT_NOT_ALLOWED';

export function isBulkNotAllowed(err: unknown): boolean {
  return err instanceof ApiError && err.code === BULK_NOT_ALLOWED;
}

/**
 * The three bulk endpoints. Plain async functions rather than mutations:
 * the import page drives them in chunks with its own progress and error
 * handling. Chunk sizes are the caller's job (see lib/bulk-import/batch).
 */
export function useBulkApi() {
  const api = useApi();
  const qc = useQueryClient();
  return useMemo(
    () => ({
      check: (items: BulkItem[]) => api.post<BulkCheckResponse>('/portal/bulk/check', { items }),
      messaged: (items: Array<BulkItem & { note?: string }>) =>
        api.post<BulkMessagedResponse>('/portal/bulk/messaged', { items }),
      log: (items: BulkLogItem[], files: Map<string, File>) => {
        const form = new FormData();
        form.append('items', JSON.stringify(items));
        for (const [field, file] of files) form.append(field, file, file.name);
        return api.postForm<BulkLogResponse>('/portal/bulk/log', form);
      },
      /** Refresh every list a bulk run can change. */
      invalidate: () => {
        for (const key of [
          ['invites'],
          ['dashboard'],
          ['prospect-search'],
          ['portal', 'outreach-history'],
          ['portal', 'discovery-prospects'],
          ['inviter', 'creator-requests'],
        ]) {
          void qc.invalidateQueries({ queryKey: key });
        }
      },
    }),
    [api, qc],
  );
}

export type BulkApi = ReturnType<typeof useBulkApi>;
